/* ============================================================
   om-pdp.js — OldMoney型PDP構造 用のふるまい
   すべてカスタム要素なので、テーマエディタでセクションを
   追加・再読み込みしても自動で初期化されます。
   他セクションの挙動には触れません。
   ============================================================ */

/* ------------------------------------------------------------
   モバイルギャラリーのドット
   ------------------------------------------------------------ */
if (!customElements.get('om-gallery-dots')) {
  customElements.define(
    'om-gallery-dots',
    class OmGalleryDots extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.scroller = null;
        this.raf = null;
        this.setup();
      }

      disconnectedCallback() {
        if (this.scroller && this.onScroll) {
          this.scroller.removeEventListener('scroll', this.onScroll);
        }
        if (this.mo) this.mo.disconnect();
      }

      setup() {
        const gallery = document.getElementById('MediaGallery-' + this.sectionId);
        if (!gallery) return;
        this.scroller = gallery.querySelector('.product__media-list');
        if (!this.scroller) return;

        this.render();

        this.onScroll = () => {
          if (this.raf) return;
          this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.syncActive();
          });
        };
        this.scroller.addEventListener('scroll', this.onScroll, { passive: true });

        // 画像の入れ替え（バリエーション変更）に追従
        this.mo = new MutationObserver(() => this.render());
        this.mo.observe(this.scroller, { childList: true });
      }

      get slides() {
        return Array.from(this.scroller.querySelectorAll('li[data-media-id]'));
      }

      render() {
        const slides = this.slides;
        if (slides.length < 2) {
          this.innerHTML = '';
          return;
        }
        const list = document.createElement('ul');
        list.className = 'om-dots__list';
        slides.forEach((slide, i) => {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'om-dots__btn';
          btn.setAttribute('aria-label', i + 1 + ' / ' + slides.length);
          btn.addEventListener('click', () => {
            this.scroller.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
          });
          li.appendChild(btn);
          list.appendChild(li);
        });
        this.innerHTML = '';
        this.appendChild(list);
        this.syncActive();
      }

      syncActive() {
        const buttons = Array.from(this.querySelectorAll('.om-dots__btn'));
        if (!buttons.length) return;
        const slides = this.slides;
        const mid = this.scroller.scrollLeft + this.scroller.clientWidth / 2;
        let index = 0;
        slides.forEach((slide, i) => {
          if (slide.offsetLeft <= mid && slide.offsetLeft + slide.offsetWidth > mid) index = i;
        });
        buttons.forEach((b, i) => {
          if (i === index) {
            b.setAttribute('aria-current', 'true');
          } else {
            b.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}

/* ------------------------------------------------------------
   追従購入導線 — メインフォームと完全同期
   実際の送信はメインの「カートに追加」ボタンを押すため、
   バリエーション・数量・カートドロワー連携がそのまま働きます。
   ------------------------------------------------------------ */
if (!customElements.get('om-sticky-atc')) {
  customElements.define(
    'om-sticky-atc',
    class OmStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('.om-sticky__btn');
        this.priceHost = this.querySelector('.om-sticky__price');
        this.subHost = this.querySelector('.om-sticky__sub');
        this.productInfo = document.getElementById('MainProduct-' + this.sectionId);
        this.mainButton = document.getElementById('ProductSubmitButton-' + this.sectionId);
        this.mainPrice = document.getElementById('price-' + this.sectionId);
        this.anchor = document.getElementById('OmAtcAnchor-' + this.sectionId);
        this.previewView = new URLSearchParams(window.location.search).get('view');
        if (this.previewView && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubUrl = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.sectionId) return;
            const url = new URL(window.location.href);
            url.searchParams.set('view', this.previewView);
            if (data.variant?.id) url.searchParams.set('variant', data.variant.id);
            window.history.replaceState({}, '', url.pathname + url.search);
          });
        }

        if (this.button && this.mainButton) {
          this.button.addEventListener('click', () => {
            if (this.button.hasAttribute('disabled')) return;
            this.mainButton.click();
          });
        }

        this.observeVisibility();
        this.observeState();
        this.sync();
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.stateMo?.disconnect();
        this.priceMo?.disconnect();
        this.unsubUrl?.();
        if (this.onChange) this.productInfo?.removeEventListener('change', this.onChange);
      }

      observeVisibility() {
        const target = this.anchor || this.mainButton;
        if (!target || !('IntersectionObserver' in window)) {
          this.classList.add('is-visible');
          return;
        }
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
          },
          { threshold: 0 }
        );
        this.io.observe(target);
      }

      observeState() {
        if (this.mainButton) {
          this.stateMo = new MutationObserver(() => this.syncButton());
          this.stateMo.observe(this.mainButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
            childList: true,
            subtree: true,
          });
        }
        if (this.mainPrice) {
          this.priceMo = new MutationObserver(() => this.syncPrice());
          this.priceMo.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
        }
        if (this.productInfo) {
          this.onChange = () => setTimeout(() => this.sync(), 60);
          this.productInfo.addEventListener('change', this.onChange);
        }
      }

      sync() {
        this.syncButton();
        this.syncPrice();
        this.syncSelection();
      }

      syncButton() {
        if (!this.button || !this.mainButton) return;
        const disabled =
          this.mainButton.hasAttribute('disabled') ||
          this.mainButton.getAttribute('aria-disabled') === 'true';
        this.button.toggleAttribute('disabled', disabled);
        const label = this.mainButton.querySelector('span');
        if (label && label.textContent.trim()) this.button.textContent = label.textContent.trim();
      }

      syncPrice() {
        if (!this.priceHost || !this.mainPrice) return;
        this.priceHost.innerHTML = this.mainPrice.innerHTML;
      }

      syncSelection() {
        if (!this.subHost || !this.productInfo) return;
        const selects = this.productInfo.querySelector('variant-selects');
        if (!selects) {
          this.subHost.textContent = '';
          return;
        }
        const values = [];
        selects.querySelectorAll('.om-opt-label__value').forEach((el) => {
          const t = el.textContent.trim();
          if (t) values.push(t);
        });
        if (!values.length) {
          selects.querySelectorAll('select').forEach((sel) => {
            if (sel.value) values.push(sel.value);
          });
        }
        this.subHost.textContent = values.join(' / ');
      }
    }
  );
}

/* ------------------------------------------------------------
   サイズガイドなどのモーダル
   ------------------------------------------------------------ */
if (!customElements.get('om-modal')) {
  customElements.define(
    'om-modal',
    class OmModal extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.om-modal__panel');
        this.querySelectorAll('[data-om-modal-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );
        this.onKey = (e) => {
          if (e.key === 'Escape') this.close();
        };
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKey);
        document.body.style.overflow = '';
      }

      open() {
        this.hidden = false;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKey);
        this.panel?.focus?.();
      }

      close() {
        this.hidden = true;
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.onKey);
        this.opener?.focus?.();
      }
    }
  );
}

if (!customElements.get('om-modal-opener')) {
  customElements.define(
    'om-modal-opener',
    class OmModalOpener extends HTMLElement {
      connectedCallback() {
        const button = this.querySelector('button');
        if (!button) return;
        button.addEventListener('click', () => {
          const modal = document.getElementById(this.dataset.target);
          if (!modal || typeof modal.open !== 'function') return;
          modal.opener = button;
          modal.open();
        });
      }
    }
  );
}

/* ------------------------------------------------------------
   「一緒に購入」の追加ボタン
   Dawn と同じ手順でカートに追加し、カートドロワーを更新します。
   ------------------------------------------------------------ */
if (!customElements.get('om-pair-add')) {
  customElements.define(
    'om-pair-add',
    class OmPairAdd extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.button.addEventListener('click', (e) => {
          e.preventDefault();
          this.add();
        });
      }

      add() {
        const variantId = this.dataset.variantId;
        if (!variantId || this.button.hasAttribute('disabled')) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        const quantity = Number(this.closest('.om-pair-drawer')?.querySelector('.om-pair-drawer__quantity input')?.value || 1);
        formData.append('quantity', String(Math.min(99, Math.max(1, Math.floor(quantity)))));
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((s) => s.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }

        const original = this.button.textContent;
        this.button.setAttribute('disabled', 'disabled');

        const url = '/cart/add.js';
        fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        })
          .then(async (r) => {
            const response = await r.json();
            if (!r.ok) throw new Error(response.description || 'カートへの追加に失敗しました');
            return response;
          })
          .then((response) => {
            if (response.status) {
              this.button.textContent = original;
              this.button.removeAttribute('disabled');
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'om-pair-add',
                productVariantId: variantId,
                cartData: response,
              });
            }
            if (cart) {
              cart.classList.remove('is-empty');
              cart.renderContents(response);
            } else {
              this.dispatchEvent(new CustomEvent('om-pair-added', { bubbles: true }));
              const appCartButton = document.querySelector('button[aria-label="Cart"]');
              if (appCartButton) appCartButton.click();
              else window.location = (window.routes && window.routes.cart_url) || '/cart';
              return;
            }
            this.dispatchEvent(new CustomEvent('om-pair-added', { bubbles: true }));
            this.button.textContent = this.dataset.addedLabel || original;
            setTimeout(() => {
              this.button.textContent = original;
              this.button.removeAttribute('disabled');
            }, 1600);
          })
          .catch((err) => {
            console.error(err);
            this.button.setAttribute('title', err.message || 'カートへの追加に失敗しました');
            this.button.textContent = original;
            this.button.removeAttribute('disabled');
          });
      }
    }
  );
}

if (!customElements.get('om-pair-picker')) {
  customElements.define('om-pair-picker', class OmPairPicker extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('.om-pair-drawer');
      if (!this.drawer) return;
      this.opener = this.querySelector('[data-om-pair-open]');
      this.variants = [...this.querySelectorAll('.om-pair-drawer__variants option')];
      this.gallery = this.querySelector('.om-pair-drawer__photos');
      this.gallerySlides = [...this.querySelectorAll('.om-pair-drawer__photo')];
      this.onClick = (event) => {
        if (event.target.closest('[data-om-pair-open]')) this.open();
        if (event.target.closest('[data-om-pair-close]')) this.close();
        const galleryButton = event.target.closest('[data-gallery-step]');
        if (galleryButton) this.moveGallery(Number(galleryButton.dataset.galleryStep));
        if (event.target.closest('.om-pair-drawer__submit')) {
          event.preventDefault();
          event.stopPropagation();
          this.addSelected();
        }
      };
      this.onChange = (event) => {
        if (event.target.closest('.om-pair-drawer__option')) this.syncVariant();
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && !this.drawer.hidden) this.close();
        if (event.target.closest('.om-pair-drawer__photos') && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
          event.preventDefault();
          this.moveGallery(event.key === 'ArrowRight' ? 1 : -1);
        }
      };
      this.onGalleryScroll = () => this.updateGallery();
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      this.gallery?.addEventListener('scroll', this.onGalleryScroll, { passive: true });
      this.addEventListener('om-pair-added', () => this.close());
      this.syncVariant();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      this.gallery?.removeEventListener('scroll', this.onGalleryScroll);
    }
    open() {
      this.drawer.hidden = false;
      if (this.gallery) this.gallery.scrollLeft = 0;
      this.updateGallery();
      this.drawer.querySelector('.om-pair-drawer__panel')?.focus();
    }
    close() {
      this.drawer.hidden = true;
      this.opener?.focus();
    }
    moveGallery(direction) {
      const slide = this.gallerySlides[0];
      if (!slide || !this.gallery) return;
      const gap = parseFloat(getComputedStyle(this.gallery).columnGap) || 0;
      this.gallery.scrollBy({ left: direction * (slide.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }
    updateGallery() {
      if (!this.gallery || !this.gallerySlides.length) return;
      const step = this.gallerySlides[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.gallery).columnGap) || 0);
      const index = step ? Math.round(this.gallery.scrollLeft / step) : 0;
      const counter = this.querySelector('[data-gallery-counter]');
      const previous = this.querySelector('[data-gallery-step="-1"]');
      const next = this.querySelector('[data-gallery-step="1"]');
      if (counter) counter.textContent = `${Math.min(index + 1, this.gallerySlides.length)} / ${this.gallerySlides.length}`;
      if (previous) previous.disabled = this.gallery.scrollLeft < 2;
      if (next) next.disabled = this.gallery.scrollLeft + this.gallery.clientWidth >= this.gallery.scrollWidth - 2;
    }
    syncVariant() {
      const values = [...this.drawer.querySelectorAll('.om-pair-drawer__option')].map((fieldset) => {
        const value = fieldset.querySelector('input:checked')?.value || fieldset.querySelector('select')?.value;
        const label = fieldset.querySelector('[data-selected-label]');
        if (label) label.textContent = value || '';
        return value;
      });
      const option = this.variants.find((candidate) => values.every((value, index) => candidate.getAttribute('data-option-' + (index + 1)) === value));
      const button = this.drawer.querySelector('.om-pair-drawer__submit');
      if (button) {
        button.disabled = !option || option.dataset.available !== 'true';
        button.textContent = button.disabled ? '売り切れ' : 'カートに追加';
      }
      if (!option) return;
      if (button) button.dataset.variantId = option.value;
      const price = Number(option.dataset.price || 0);
      const compare = Number(option.dataset.compare || 0);
      const currency = document.querySelector('variant-selects')?.dataset.currencyCode || 'JPY';
      const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', { style: 'currency', currency }).format(cents / 100);
      const priceEl = this.drawer.querySelector('.om-pair-drawer__price strong');
      const compareEl = this.drawer.querySelector('.om-pair-drawer__price s');
      if (priceEl) priceEl.textContent = money(price);
      if (compareEl) { compareEl.hidden = compare <= price; compareEl.textContent = compare > price ? money(compare) : ''; }
    }
    async addSelected() {
      const button = this.drawer.querySelector('.om-pair-drawer__submit');
      const variantId = button?.dataset.variantId;
      if (!variantId || button.disabled) return;
      const qty = Number(this.drawer.querySelector('.om-pair-drawer__quantity input')?.value || 1);
      button.disabled = true;
      button.textContent = '追加中…';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: Math.min(99, Math.max(1, Math.floor(qty))) }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || '追加できませんでした');
        this.close();
        button.textContent = 'カートに追加しました';
        this.dispatchEvent(new CustomEvent('om-pair-added', { bubbles: true, detail: { variantId, item: result } }));
        const cart = document.querySelector('cart-drawer');
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(result);
        }
        setTimeout(() => { button.disabled = false; button.textContent = 'カートに追加'; }, 1600);
      } catch (error) {
        button.textContent = error.message || '追加できませんでした';
        button.disabled = false;
      }
    }
  });
}

/* ------------------------------------------------------------
   単品／複数購入の選択 → フォームの quantity を書き換え
   ------------------------------------------------------------ */
if (!customElements.get('om-quantity-picker')) {
  customElements.define('om-quantity-picker', class OmQuantityPicker extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="quantity"]');
      this.onClick = (event) => {
        const stepper = event.target.closest('button[data-step]');
        if (!stepper || !this.input) return;
        const value = Number(this.input.value) || 1;
        const next = Math.min(Number(this.input.max) || 99, Math.max(1, value + Number(stepper.dataset.step)));
        this.input.value = String(next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this.addEventListener('click', this.onClick);
    }
    disconnectedCallback() { this.removeEventListener('click', this.onClick); }
  });
}
if (!customElements.get('om-bundle')) {
  customElements.define(
    'om-bundle',
    class OmBundle extends HTMLElement {
      connectedCallback() {
        this.qtyInput = document.getElementById('OmQuantity-' + this.dataset.section);
        this.onVariant = ({ data }) => {
          if (data?.sectionId !== this.dataset.section || !data.variant) return;
          const unit = data.variant.price;
          const n = Number(this.querySelector('input[data-quantity]:not([data-quantity="1"])')?.dataset.quantity || 2);
          const discount = Number(this.dataset.discountPercent || 50) / 100;
          const currency = document.querySelector('variant-selects')?.dataset.currencyCode || 'JPY';
          const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', { style: 'currency', currency }).format(cents / 100);
          const single = this.querySelector('.om-bundle__opt:first-child .om-bundle__prices');
          const multi = this.querySelector('.om-bundle__opt:nth-child(2) .om-bundle__prices');
          if (single) single.textContent = money(unit);
          if (multi) multi.innerHTML = '<s>' + money(unit * n) + '</s> ' + money(unit * n - unit * (n - 1) * discount);
        };
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariant);
        }
        this.addEventListener('change', (e) => {
          const target = e.target;
          if (!target || target.type !== 'radio' || !this.qtyInput) return;
          this.qtyInput.value = target.dataset.quantity || '1';
        });
        const checked = this.querySelector('input[type="radio"]:checked');
        if (checked && this.qtyInput) this.qtyInput.value = checked.dataset.quantity || '1';
      }
      disconnectedCallback() { this.unsubVariant?.(); }
    }
  );
}

if (!customElements.get('om-feature-slider')) {
  customElements.define('om-feature-slider', class OmFeatureSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.om-feat__grid');
      this.previous = this.querySelector('[data-direction="-1"]');
      this.next = this.querySelector('[data-direction="1"]');
      if (!this.track) return;
      this.onClick = (event) => {
        const button = event.target.closest('[data-direction]');
        if (!button) return;
        const item = this.track.querySelector('.om-feat__item');
        if (!item) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        this.track.scrollBy({ left: Number(button.dataset.direction) * (item.getBoundingClientRect().width + gap), behavior: 'smooth' });
      };
      this.onScroll = () => {
        if (this.previous) this.previous.disabled = this.track.scrollLeft < 2;
        if (this.next) this.next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      };
      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
      this.onScroll();
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }
  });
}
